import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  // 45s browser-side ceiling so a stuck Stripe call never leaves the
  // customer staring at a spinner. Cloudflare's own origin timeout is
  // ~100s; we cut in first so the failure message is OURS, not their
  // "Bad Gateway" HTML page.
  timeout: 45000,
});

// ---------------------------------------------------------------------------
// Universal error normaliser — every place in the app does
//   `toast.error(e?.response?.data?.detail || "Something went wrong")`
// which shows the fallback whenever the request never reached the server
// (network drop, timeout, CORS pre-flight fail, Cloudflare 502 HTML page).
// The customer then sees a useless generic message and thinks the site
// is broken.  Here we inject a synthetic `response.data.detail` so those
// call sites automatically render an accurate, actionable string.
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If the request never got a response (network drop, timeout, CORS,
    // Cloudflare 5xx HTML page) synthesise a friendly detail for the
    // toast handlers.
    if (!error.response) {
      const isTimeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
      const friendly = isTimeout
        ? "Our server took too long to respond. Nothing has changed on your account — please try again in a few moments."
        : "We couldn't reach the server. Please check your connection and try again. If this keeps happening, email orders@thebutcherbox.com.au.";
      error.response = { status: 0, data: { detail: friendly, kind: isTimeout ? "timeout" : "network" } };
      return Promise.reject(error);
    }
    // If the server returned a non-JSON body (e.g. Cloudflare 502 HTML)
    // give the caller a clean detail rather than "[object Object]".
    const d = error.response.data;
    if (d && typeof d === "object" && !("detail" in d) && !("message" in d) && !("error" in d)) {
      error.response.data = {
        ...d,
        detail: `Server error ${error.response.status}. Please try again in a moment. If this keeps happening, email orders@thebutcherbox.com.au.`,
      };
    } else if (typeof d === "string" && d.length > 200) {
      // Likely an HTML error page — shorten so the toast doesn't overflow.
      error.response.data = {
        detail: `Server error ${error.response.status}. Please try again in a moment.`,
      };
    }
    return Promise.reject(error);
  },
);
